import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import styled from 'styled-components';
import { Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { fetchOrganizationUsers, deleteOrganizationUser } from '../../../services/Http/Api';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import { MdBadge, MdPeople, MdLock, MdAdd, MdEdit, MdDelete } from 'react-icons/md';
import { toast } from 'react-toastify';
import RolesManagement from './RolesManagement';
import StaffModal from './components/StaffModal';
import MainButton from '../../../shared/layout/buttons/MainButton';
import IconButton from '../../../shared/layout/buttons/IconButton';

const TabsContainer = styled.div`
    display: flex;
    gap: 0.25rem;
    margin-bottom: 1.5rem;
    background: #f3f4f6;
    padding: 0.25rem;
    border-radius: 10px;
    width: fit-content;
`;

const Tab = styled.button`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1.25rem;
    border: none;
    background: ${props => props.$active ? 'white' : 'transparent'};
    color: ${props => props.$active ? '#821692' : '#6b7280'};
    font-size: 0.875rem;
    font-weight: 500;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: ${props => props.$active ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'};

    &:hover {
        color: ${props => props.$active ? '#821692' : '#374151'};
        background: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
    }

    svg {
        font-size: 1.125rem;
    }
`;

const HeaderActions = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
`;

const ActionWrapper = styled.div`
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
`;

const RoleBadge = styled.span`
    padding: 0.25rem 0.625rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 500;
    background: rgba(130, 22, 146, 0.1);
    color: #821692;
    display: inline-block;
    margin-right: 0.25rem;
`;

const OrganizationUsers = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState('staff');
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState('id');
    const [sortDirection, setSortDirection] = useState('asc');
    const [showStaffModal, setShowStaffModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedUser, setSelectedUser] = useState(null);

    const customStyles = {
        table: { style: { backgroundColor: 'transparent' } },
        headRow: {
            style: {
                backgroundColor: '#f9fafb',
                minHeight: '52px',
                borderBottom: '1px solid #e5e7eb',
            },
        },
        headCells: {
            style: {
                fontSize: '12px',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
            },
        },
        rows: {
            style: {
                minHeight: '72px',
                '&:not(:last-of-type)': {
                    borderBottom: '1px solid #f3f4f6',
                },
                '&:hover': {
                    backgroundColor: '#f9fafb !important',
                    transition: 'background-color 0.2s',
                },
            },
        },
    };

    const loadUsers = async (page) => {
        setLoading(true);
        try {
            const response = await fetchOrganizationUsers(page, perPage, sortBy, sortDirection);
            setUsers(response.users || []);
            setTotalRows(response.total || 0);
            setCurrentPage(response.currentPage || page);
        } catch (error) {
            console.error('Error fetching organization users:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'staff') {
            loadUsers(currentPage);
        }
    }, [currentPage, perPage, sortBy, sortDirection, activeTab]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePerRowsChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        loadUsers(page);
    };

    const handleSort = (column, direction) => {
        setSortBy(column.key);
        setSortDirection(direction);
    };

    const handleAddStaff = () => {
        setModalMode('create');
        setSelectedUser(null);
        setShowStaffModal(true);
    };

    const handleEditStaff = (user) => {
        setModalMode('edit');
        setSelectedUser(user);
        setShowStaffModal(true);
    };

    const handleDeleteStaff = async (user) => {
        if (!window.confirm(t('OrgUsers.deleteConfirm'))) {
            return;
        }

        try {
            await deleteOrganizationUser(user.id);
            toast.success(t('OrgUsers.deleteSuccess'));
            loadUsers(currentPage);
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error(t('OrgUsers.deleteError'));
        }
    };

    const handleStaffModalClose = () => {
        setShowStaffModal(false);
        setSelectedUser(null);
        setModalMode('create');
    };

    const handleStaffSaved = () => {
        loadUsers(currentPage);
        handleStaffModalClose();
    };

    const columns = [
        {
            name: t('OrgUsers.columns.name'),
            key: 'name',
            selector: row => `${row.name || ''} ${row.surname || ''}`.trim() || 'N/A',
            sortable: true,
        },
        {
            name: t('OrgUsers.columns.email'),
            key: 'email',
            selector: row => row.email || 'N/A',
            sortable: true,
        },
        {
            name: t('OrgUsers.columns.role'),
            cell: row => (
                <div>
                    {row.organization_roles?.map((role) => (
                        <RoleBadge key={role.id}>{role.display_name}</RoleBadge>
                    )) || row.roles?.map((role) => (
                        <RoleBadge key={role.id}>{role.name}</RoleBadge>
                    )) || 'N/A'}
                </div>
            ),
        },
        {
            name: t('OrgUsers.columns.createdAt'),
            key: 'created_at',
            selector: row => row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A',
            sortable: true,
        },
        {
            name: '',
            cell: row => (
                <ActionWrapper>
                    <IconButton noBg onClick={() => handleEditStaff(row)}>
                        <MdEdit style={{ color: '#6b7280' }} />
                    </IconButton>
                    <IconButton noBg onClick={() => handleDeleteStaff(row)}>
                        <MdDelete style={{ color: '#ef4444' }} />
                    </IconButton>
                </ActionWrapper>
            ),
            ignoreRowClick: true,
            width: '100px',
        },
    ];

    return (
        <PageContainer>
            <PageHeader>
                <PageTitle icon={<MdBadge size={28} />}>{t('OrgUsers.title')}</PageTitle>
            </PageHeader>
            <PageContent>
                <TabsContainer>
                    <Tab
                        $active={activeTab === 'staff'}
                        onClick={() => setActiveTab('staff')}
                    >
                        <MdPeople />
                        {t('OrgUsers.tabs.staff')}
                    </Tab>
                    <Tab
                        $active={activeTab === 'roles'}
                        onClick={() => setActiveTab('roles')}
                    >
                        <MdLock />
                        {t('OrgUsers.tabs.roles')}
                    </Tab>
                </TabsContainer>

                {activeTab === 'staff' && (
                    <>
                        <HeaderActions>
                            <div></div>
                            <MainButton onClick={handleAddStaff}>
                                <MdAdd style={{ marginRight: '8px' }} />
                                {t('OrgUsers.addStaff')}
                            </MainButton>
                        </HeaderActions>
                        <StyledCard noPadding>
                            <DataTable
                                columns={columns}
                                data={users}
                                pagination
                                paginationServer
                                paginationTotalRows={totalRows}
                                onChangePage={handlePageChange}
                                onChangeRowsPerPage={handlePerRowsChange}
                                progressPending={loading}
                                progressComponent={<StyledSpinner />}
                                onSort={handleSort}
                                sortServer
                                highlightOnHover
                                noHeader
                                customStyles={customStyles}
                            />
                        </StyledCard>
                    </>
                )}

                {activeTab === 'roles' && (
                    <RolesManagement />
                )}
            </PageContent>

            {showStaffModal && (
                <StaffModal
                    isOpen={showStaffModal}
                    onClose={handleStaffModalClose}
                    onSave={handleStaffSaved}
                    mode={modalMode}
                    user={selectedUser}
                />
            )}
        </PageContainer>
    );
};

export default OrganizationUsers;

const StyledSpinner = styled(Spinner)`
    color: rgba(130, 22, 146, 1);
`;
